/**
 * Main layout component - Styled according to style.md
 */
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  CSSObject,
  Theme,
  styled,
  useMediaQuery,
  Fade,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ChevronRight,
  ChevronLeft,
  Home,
  LayoutDashboard,
  Users,
  ClipboardList,
  GraduationCap,
  BarChart3,
  Settings,
  User,
  LogOut,
  Moon,
  Sun,
  Monitor,
  Briefcase,
  UserSearch,
  GitBranch,
  Building2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useThemeMode } from '../../context/ThemeContext';
import GradientBackground from '../common/GradientBackground';
import IdleTimeoutHandler from '../common/IdleTimeoutHandler';

// System type for toggle
type SystemType = 'ems' | 'ats';

// --- Configuration from your original file ---
const drawerWidth = 200;
const collapsedWidth = 72; // Width when closed based on your icon size
const waterFlowTransition = 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1), top 0.5s cubic-bezier(0.4, 0, 0.2, 1), height 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.5s cubic-bezier(0.4, 0, 0.2, 1), padding 0.5s cubic-bezier(0.4, 0, 0.2, 1)';

// --- 1. Define Mixins (The States) ---

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  top: '80px',
  height: 'calc(100vh - 100px)',
  borderRadius: '16px',
  transform: 'translateY(0)',
  transition: waterFlowTransition,
  overflowX: 'hidden',

  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.06)'
    : '1px solid rgba(0, 0, 0, 0.04)',

  background: theme.palette.mode === 'dark'
    ? 'rgba(28, 28, 30, 0.95)'
    : 'rgba(255, 255, 255, 0.85)',

  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',

  boxShadow: theme.palette.mode === 'dark'
    ? '0px 4px 16px -4px rgba(0, 0, 0, 0.3)'
    : '0px 4px 20px -4px rgba(0, 0, 0, 0.06)',
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: collapsedWidth,
  top: '50%',
  height: 'auto',
  paddingBottom: '8px',
  paddingTop: '8px',
  borderRadius: '14px',
  transform: 'translateY(-50%)',
  transition: waterFlowTransition,
  overflowX: 'hidden',

  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.06)'
    : '1px solid rgba(0, 0, 0, 0.04)',

  background: theme.palette.mode === 'dark'
    ? 'rgba(28, 28, 30, 0.95)'
    : 'rgba(255, 255, 255, 0.85)',

  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',

  boxShadow: theme.palette.mode === 'dark'
    ? '0px 4px 16px -4px rgba(0, 0, 0, 0.3)'
    : '0px 4px 20px -4px rgba(0, 0, 0, 0.06)',
});

// --- 2. Styled Components ---

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    // We apply the mixins to the PAPER element (the actual visible box)
    '& .MuiDrawer-paper': {
      position: 'fixed',
      left: 0,
      marginLeft: theme.spacing(1.5), // Original mx: 1.5
      zIndex: 10,
      ...(open && openedMixin(theme)),
      ...(!open && closedMixin(theme)),
    },
  }),
);

// This handles the MAIN content transition
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginTop: '60px', // AppBar height
  minHeight: 'calc(100vh - 60px)',
  overflow: 'hidden',
  // Critical: This margin transition MUST match the Drawer transition exactly
  transition: waterFlowTransition,
  marginLeft: !open ? -75 : collapsedWidth, // Adjust 20px for gap
  width: `calc(100% - ${open ? drawerWidth + 20 : collapsedWidth + 20}px)`,
}));

const Layout: React.FC = () => {
  const isLargeScreen = useMediaQuery('(min-width:426px)');
  const isSmallScreen = useMediaQuery('(max-width:768px)');
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [themeAnchor, setThemeAnchor] = useState<null | HTMLElement>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuth();
  const { themeMode, setThemeMode, effectiveTheme } = useThemeMode();

  // Determine current system based on URL path (primary source of truth)
  const getSystemFromPath = (pathname: string): SystemType => {
    return pathname.startsWith('/ats') ? 'ats' : 'ems';
  };

  const [currentSystem, setCurrentSystem] = useState<SystemType>(() => {
    // Initialize from URL path, not localStorage
    return getSystemFromPath(window.location.pathname);
  });

  // Sync toggle state with URL path changes (e.g., browser back/forward)
  useEffect(() => {
    const systemFromPath = getSystemFromPath(location.pathname);
    if (currentSystem !== systemFromPath) {
      setCurrentSystem(systemFromPath);
    }
  }, [location.pathname, currentSystem]);

  // Sync current system to localStorage for persistence
  useEffect(() => {
    localStorage.setItem('currentSystem', currentSystem);
  }, [currentSystem]);

  // Handle system toggle change
  const handleSystemChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSystem: SystemType | null
  ) => {
    if (newSystem !== null) {
      setCurrentSystem(newSystem);
      // Navigate to respective dashboard
      if (newSystem === 'ats') {
        navigate('/ats');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setProfileAnchor(event.currentTarget);
  const handleThemeMenuOpen = (event: React.MouseEvent<HTMLElement>) => setThemeAnchor(event.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchor(null);
  const handleThemeMenuClose = () => setThemeAnchor(null);

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const handleThemeChange = (mode: 'light' | 'dark' | 'system') => {
    setThemeMode(mode);
    handleThemeMenuClose();
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };


  // EMS Menu Items
  const emsMenuItems = [
    { text: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Employees', icon: Users, path: '/employees', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Organization', icon: GitBranch, path: '/organization', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Clients', icon: Building2, path: '/clients', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Projects', icon: ClipboardList, path: '/projects', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Onboarding', icon: GraduationCap, path: '/onboarding', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Reports', icon: BarChart3, path: '/reports', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] },
  ];

  // ATS Menu Items
  const atsMenuItems = [
    { text: 'Dashboard', icon: LayoutDashboard, path: '/ats', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] },
    { text: 'Open Roles', icon: Briefcase, path: '/ats/open-roles', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] },
    { text: 'Candidates', icon: UserSearch, path: '/ats/candidates', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] },
  ];

  // Select menu items based on current system
  const menuItems = currentSystem === 'ats' ? atsMenuItems : emsMenuItems;

  // Generate breadcrumbs based on current path
  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);

    // Path label mapping
    const pathLabels: Record<string, string> = {
      'dashboard': 'Dashboard',
      'employees': 'Employees',
      'projects': 'Projects',
      'clients': 'Clients',
      'organization': 'Organization Hierarchy',
      'onboarding': 'Onboarding',
      'reports': 'Reports',
      'users': 'Admin Settings',
      'profile': 'Profile',
      'ats': 'ATS Dashboard',
      'open-roles': 'Open Roles',
      'candidates': 'Candidates',
    };

    // Build breadcrumb items
    const breadcrumbItems: { label: string; path: string; isLast: boolean }[] = [];

    pathnames.forEach((segment, index) => {
      // Skip the root-level segments so they don't repeat after "Home"
      if (segment === 'dashboard' || segment === 'ats' || segment === 'home') {
        return;
      }

      // Skip numeric segments (IDs) - e.g., "1020", "1038", "5"
      if (/^\d+$/.test(segment)) {
        return;
      }

      // Skip "pipeline" and "applications" segments
      if (segment === 'pipeline' || segment === 'applications') {
        return;
      }

      const path = `/${pathnames.slice(0, index + 1).join('/')}`;
      const label = pathLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      const isLast = index === pathnames.length - 1;

      breadcrumbItems.push({ label, path, isLast });
    });

    return breadcrumbItems;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <GradientBackground />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: 60,
          bgcolor: effectiveTheme === 'dark' ? 'rgba(26, 26, 26, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: effectiveTheme === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ height: 60, px: 2.5, minHeight: '60px !important' }}>
          <Box
            component="img"
            src={isSmallScreen ? 'DatafactZ logo.png' : (effectiveTheme === 'light' ? 'Datafact Z New Logo Dark.png' : 'Datafact Z New Logo.png')}
            alt="DataFactz"
            sx={{
              height: '1.75rem',
              mr: 3,
              ml: 2,
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={() => navigate(currentSystem === 'ems' ? '/dashboard' : '/ats')}
          />

          {/* Divider */}
          <Box
            sx={{
              width: '1px',
              height: '24px',
              bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
              mr: 3,
            }}
          />

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              color: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : '#1D1D1F',
              flexGrow: 1,
              fontWeight: 500,
              fontSize: '1rem',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '-0.01em',
            }}
          >
            {currentSystem === 'ems'
              ? (isSmallScreen ? 'EMS' : 'Employee Management System') : (isSmallScreen ? 'ATS' : 'Application Tracking System')
            }
          </Typography>

          {isSmallScreen && <Box sx={{ flexGrow: 1 }} />}

          {/* EMS/ATS System Toggle */}
          <ToggleButtonGroup
            value={currentSystem}
            exclusive
            onChange={handleSystemChange}
            size="small"
            sx={{
              mr: 1.5,
              bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
              borderRadius: '8px',
              padding: '3px',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '6px !important',
                px: 1.5,
                py: 0.4,
                fontSize: '0.75rem',
                fontWeight: 600,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                textTransform: 'none',
                color: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)',
                transition: 'all 0.15s ease',
                '&.Mui-selected': {
                  bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'white',
                  color: effectiveTheme === 'dark' ? 'white' : '#1D1D1F',
                  boxShadow: effectiveTheme === 'dark' ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
                  '&:hover': {
                    bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'white',
                  },
                },
                '&:hover': {
                  bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                },
              },
            }}
          >
            <ToggleButton value="ems">EMS</ToggleButton>
            <ToggleButton value="ats">ATS</ToggleButton>
          </ToggleButtonGroup>

          <IconButton
            onClick={handleThemeMenuOpen}
            sx={{
              mr: 1,
              color: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
              width: 36,
              height: 36,
              '&:hover': {
                bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {effectiveTheme === 'dark' ? <Moon size={18} strokeWidth={1.75} /> : <Sun size={18} strokeWidth={1.75} />}
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleProfileMenuOpen}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: 'primary.main',
                fontWeight: 600,
                fontSize: '0.875rem',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                color: 'white',
              }}
            >
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer variant="permanent" open={drawerOpen && !isSmallScreen}>

        {/* Toggle Button Inside Drawer */}
        {!isSmallScreen && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: drawerOpen ? 'flex-end' : 'center', py: 1.5, px: drawerOpen ? 1.5 : 0, width: '100%' }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                bgcolor: effectiveTheme === 'dark'
                  ? 'rgba(255, 255, 255, 0.04)'
                  : 'rgba(0, 0, 0, 0.03)',
                color: effectiveTheme === 'dark'
                  ? 'rgba(255, 255, 255, 0.5)'
                  : '#9CA3AF',
                width: 32,
                height: 32,
                borderRadius: '8px',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: effectiveTheme === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.06)',
                  color: effectiveTheme === 'dark'
                    ? 'rgba(255, 255, 255, 0.8)'
                    : '#6B7280',
                }
              }}
            >
              {drawerOpen ? <ChevronLeft size={16} strokeWidth={1.75} /> : <ChevronRight size={16} strokeWidth={1.75} />}
            </IconButton>
          </Box>
        )}

        <Fade in={true} timeout={300} key={currentSystem}>
          <List sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, width: '100%', px: 0.75, pt: 0.5 }}>
            {menuItems
              .filter((item) => hasRole(item.roles))
              .map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                const isOpen = drawerOpen && !isSmallScreen;

              return (
                <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.25 }}>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      minHeight: isOpen ? 44 : 44,
                      width: isOpen ? '90%' : '44px',
                      height: isOpen ? 'auto' : '44px',
                      mx: 'auto',
                      justifyContent: isOpen ? 'flex-start' : 'center',
                      alignItems: 'center',
                      borderRadius: '10px',
                      background: isActive
                        ? (effectiveTheme === 'dark'
                            ? 'rgba(249, 115, 22, 0.15)'
                            : 'rgba(249, 115, 22, 0.1)')
                        : 'transparent',
                      color: isActive
                        ? '#F97316'
                        : (effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#6B7280'),
                      boxShadow: 'none',
                      transition: 'all 0.15s ease',
                      px: isOpen ? 2 : 0,
                      py: isOpen ? 0.75 : 0,
                      '&:hover': {
                        background: isActive
                          ? (effectiveTheme === 'dark'
                              ? 'rgba(249, 115, 22, 0.2)'
                              : 'rgba(249, 115, 22, 0.12)')
                          : (effectiveTheme === 'dark'
                              ? 'rgba(255, 255, 255, 0.06)'
                              : 'rgba(0, 0, 0, 0.04)'),
                        color: isActive
                          ? '#F97316'
                          : (effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#1D1D1F'),
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        minWidth: 0,
                        mr: isOpen ? 1.5 : 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon size={isOpen ? 18 : 20} strokeWidth={1.75} />
                    </Box>
                    <Typography
                      sx={{
                        opacity: isOpen ? 1 : 0,
                        width: isOpen ? 'auto' : 0,
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '0.875rem',
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                        letterSpacing: '-0.01em',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        transition: 'opacity 0.15s ease',
                      }}
                    >
                      {item.text}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              );
              })}
          </List>
        </Fade>
      </Drawer>

      {/* Main Content using the Synchronized styled component */}
      <Main open={drawerOpen && !isSmallScreen}>
        {/* Breadcrumb Navigation */}
        {breadcrumbs.length > 0 && (
          <Breadcrumbs
            separator={<ChevronRight size={16} strokeWidth={1.75} />}
            aria-label="breadcrumb"
            sx={{
              mb: 2,
              '& .MuiBreadcrumbs-ol': {
                flexWrap: 'nowrap',
              },
              '& .MuiBreadcrumbs-separator': {
                mx: 1,
                color: 'text.secondary',
              },
            }}
          >
            <Link
              underline="hover"
              color="text.secondary"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(currentSystem === 'ats' ? '/ats' : '/dashboard');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.875rem',
                gap: 0.5,
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <Home size={16} strokeWidth={1.75} />
              Home
            </Link>
            {breadcrumbs.map((crumb) => (
              crumb.isLast ? (
                <Typography
                  key={crumb.path}
                  color="text.primary"
                  sx={{ fontSize: '0.875rem', fontWeight: 600 }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.path}
                  underline="hover"
                  color="text.secondary"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(crumb.path);
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {crumb.label}
                </Link>
              )
            ))}
          </Breadcrumbs>
        )}
        <Outlet />
      </Main>

      {/* Menus... */}
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={handleProfileMenuClose}
        sx={{ mt: 1 }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }} sx={{ gap: 1.5 }}>
          <User size={18} strokeWidth={1.75} /> Profile
        </MenuItem>
        {hasRole(['ADMIN']) && (
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/users'); }} sx={{ gap: 1.5 }}>
            <Settings size={18} strokeWidth={1.75} /> Admin Settings
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ gap: 1.5 }}>
          <LogOut size={18} strokeWidth={1.75} /> Logout
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={themeAnchor}
        open={Boolean(themeAnchor)}
        onClose={handleThemeMenuClose}
        sx={{ mt: 1 }}
      >
        <MenuItem onClick={() => handleThemeChange('light')} selected={themeMode === 'light'} sx={{ gap: 1.5 }}>
          <Sun size={18} strokeWidth={1.75} /> Light
        </MenuItem>
        <MenuItem onClick={() => handleThemeChange('dark')} selected={themeMode === 'dark'} sx={{ gap: 1.5 }}>
          <Moon size={18} strokeWidth={1.75} /> Dark
        </MenuItem>
        <MenuItem onClick={() => handleThemeChange('system')} selected={themeMode === 'system'} sx={{ gap: 1.5 }}>
          <Monitor size={18} strokeWidth={1.75} /> System
        </MenuItem>
      </Menu>

      {/* Idle Timeout Handler - logs out after 30 minutes of inactivity */}
      <IdleTimeoutHandler timeout={30} warningTime={60} />
    </Box>
  );
};

export default Layout;